import { z } from 'zod';

/**
 * Age gate (PRD section 9). Sign-up asks for month and year of birth only; under-13s
 * cannot hold an account (they can still play as guests), and 13–17 accounts are
 * flagged as teen so features can be restricted for them.
 */
export const MIN_ACCOUNT_AGE = 13;
export const ADULT_AGE = 18;

export type AgeBand = 'child' | 'teen' | 'adult';

/** `YYYY-MM`, e.g. `2008-03`. */
export const birthMonthSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use the format YYYY-MM')
  .refine((value) => Number(value.slice(0, 4)) >= 1900, 'Year must be 1900 or later')
  .refine((value) => monthIndex(value) <= monthIndex(new Date()), 'Date is in the future');

/** Accepts `YYYY-MM` or a stored `YYYY-MM-DD` date (day ignored). */
function monthIndex(value: string | Date): number {
  if (value instanceof Date) return value.getUTCFullYear() * 12 + value.getUTCMonth();
  return Number(value.slice(0, 4)) * 12 + Number(value.slice(5, 7)) - 1;
}

/**
 * Whole years of age, rounded down conservatively: with only a birth month, we assume
 * the latest possible birthday (end of that month), so nobody is counted older than they are.
 */
export function ageInYears(birthMonth: string, now: Date = new Date()): number {
  const fullMonths = monthIndex(now) - monthIndex(birthMonth) - 1;
  return Math.max(0, Math.floor(fullMonths / 12));
}

export function ageBand(birthMonth: string, now: Date = new Date()): AgeBand {
  const age = ageInYears(birthMonth, now);
  if (age < MIN_ACCOUNT_AGE) return 'child';
  return age < ADULT_AGE ? 'teen' : 'adult';
}

/** `YYYY-MM` → the `YYYY-MM-01` date stored in `users.birth_month`. */
export function birthMonthToDate(birthMonth: string): string {
  return `${birthMonth}-01`;
}
